import React from "react";
import { Layout, Button, Popover, Avatar, Popconfirm, Select } from "antd";
import {
  useNavigate,
  useRevalidator,
  useLocation,
  useRouteLoaderData,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { auth } from "~/lib/firebase";
import type { MainLayoutLoaderData } from "~/components/Layout/MainLayout/MainLayout";

import {
  UserOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
  RedoOutlined,
  BookOutlined,
  GlobalOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { clearCookiesData } from "~/utils";
import { GuideButton } from "~/components/Tour";
import { useAppGuide } from "~/components/AppGuide";
import { useMediaQuery } from "@mui/material";

const { Header } = Layout;

const TopNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const revalidator = useRevalidator();
  const { i18n, t } = useTranslation();
  const layoutData = useRouteLoaderData("main") as MainLayoutLoaderData | undefined;
  const profile = layoutData?.profile ?? null;
  const { openGuide } = useAppGuide();
  const isMobile = useMediaQuery("(max-width: 600px)");

  const handleLanguageChange = (newLanguage: string) => {
    // Change the language for the current session
    i18n.changeLanguage(newLanguage);

    // Persist the language so it is restored on reload (matches src/i18n.ts)
    Cookies.set("language", newLanguage, { expires: 365 });
  };

  const handleForceReload = () => {
    // Clear cookies data
    clearCookiesData();

    // Get current search params and add force=true
    const currentSearchParams = new URLSearchParams(location.search);
    currentSearchParams.set("force", "true");

    // Navigate to the same path with updated search params
    navigate({
      pathname: location.pathname,
      search: currentSearchParams.toString(),
    });
  };

  const handleLogout = async () => {
    try {
      // Clear cookies data first
      clearCookiesData();
      // Sign out from Firebase
      await auth.signOut();
      // Revalidate all loaders to update the UI with the new auth state
      // This will cause all route loaders to re-run and update the page accordingly
      revalidator.revalidate();
    } catch (error) {
      console.error("Error occurred during logout:", error);
    }
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === "account") {
      navigate("/account");
    } else if (key === "settings") {
      navigate("/settings");
    } else if (key === "signout") {
      handleLogout();
    } else if (key === "help") {
      navigate("/help");
    }
  };

  const userContent = (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {profile?.username ? (
          <Avatar
            size={64}
            src={
              profile.profilePictureUrl ??
              `https://robohash.org/${profile.username}?size=200x200&bgset=bg2`
            }
            alt={profile.username}
          />
        ) : (
          <Avatar size={64} icon={<UserOutlined />} />
        )}
        <div style={{ marginTop: "0.5rem", fontWeight: "bold" }}>
          {profile?.username ??
            auth.currentUser?.displayName ??
            auth.currentUser?.email}
        </div>
        <div style={{ marginTop: "0.5rem" }}>{profile?.email ?? auth.currentUser?.email}</div>
      </div>
      <div style={{ marginTop: "1rem" }}>
        <Button
          type="link"
          icon={<UserOutlined />}
          onClick={() => handleMenuClick({ key: "account" })}
        >
          Account
        </Button>
        <Button
          type="link"
          icon={<SettingOutlined />}
          onClick={() => handleMenuClick({ key: "settings" })}
        >
          Settings
        </Button>
        <Popconfirm
          title="Confirm sign out"
          description="Are you sure you want to sign out?"
          onConfirm={handleLogout}
          okText={t("Sign_out")}
          cancelText={t("Cancel")}
        >
          <Button type="link" icon={<UserOutlined />}>
            {t("Sign_out")}
          </Button>
        </Popconfirm>
        <Button
          type="link"
          icon={<QuestionCircleOutlined />}
          onClick={() => handleMenuClick({ key: "help" })}
        >
          {t("Help")}
        </Button>
      </div>
    </div>
  );

  return (
    <Header
      style={{
        background: "#fff",
        padding: 0,
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        zIndex: 100,
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "thin",
      }}
    >
      <div
        className="logo"
        style={{
          height: "100px",
          width: "60px",
          minWidth: "60px",
          paddingLeft: "0.5rem",
          flexShrink: 0,
        }}
      >
        <img
          style={{
            height: "100%",
            width: "100%",
            objectFit: "contain",
            cursor: "pointer",
          }}
          src="/logo1.png"
          alt="Bomani"
          onClick={() => navigate("/")}
        />
      </div>
      <Select
        style={{
          marginLeft: "0.5rem",
          minWidth: isMobile ? 50 : 100,
          flexShrink: 0,
        }}
        value={i18n.language}
        onChange={(value) => {
          handleLanguageChange(value);
        }}
        suffixIcon={isMobile ? <GlobalOutlined /> : undefined}
        options={
          isMobile
            ? [
                { label: "EN", value: "eng" },
                { label: "FR", value: "fr" },
                { label: "ES", value: "es" },
              ]
            : [
                { label: "English", value: "eng" },
                { label: "Français", value: "fr" },
                { label: "Español", value: "es" },
              ]
        }
      />
      <div style={{ flex: 1, minWidth: "0.5rem" }} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          paddingRight: "1rem",
          flexShrink: 0,
        }}
      >
        <Button
          type="primary"
          icon={<RedoOutlined />}
          style={{ marginRight: "0.5rem" }}
          onClick={handleForceReload}
          title={t("Refresh")}
        />
        <GuideButton />
        <Button
          icon={<BookOutlined />}
          style={{
            marginRight: "0.5rem",
            background: "#722ed1",
            borderColor: "#722ed1",
            color: "white",
          }}
          onClick={() => openGuide()}
          title={t("AppGuide_Button")}
        >
          {!isMobile && t("AppGuide_Button")}
        </Button>
        <Button
          type="primary"
          icon={<InboxOutlined />}
          style={{ marginRight: "0.5rem" }}
          onClick={() => navigate("/messages")}
        />
        <Popover placement="bottomRight" content={userContent} trigger="click">
          <Button
            type="primary"
            icon={<UserOutlined />}
            style={{ marginRight: "0.5rem" }}
          />
        </Popover>
      </div>
    </Header>
  );
};

export default TopNavigation;
